import React, { useCallback, useEffect, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useSearchParams } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import { getLogsColumns } from "./getLogsColumns";
import LogDetailDialog from "./LogDetailDialog";
import SystemLogService from "./SystemLogService";
import Logger from "../../../services/Logger";

const SYSTEM_LOG_LEVELS = ["INFO", "WARN", "ERROR", "SYSTEM"];
const LOGS_FILTER_STORAGE_KEY = "admin.logs.filter";
const AUTO_REFRESH_STORAGE_KEY = "admin.logs.autoRefresh";
const AUTO_REFRESH_INTERVAL_MS = 60_000;

const loadFiltersFromStorage = () => {
    try {
        const stored = localStorage.getItem(LOGS_FILTER_STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            return {
                level: parsed.level ?? "",
                source: parsed.source ?? "",
                message: parsed.message ?? "",
                excludeScheduler: parsed.excludeScheduler !== false
            };
        }
    } catch (e) {
        Logger.error("Error reading logs filter from localStorage:", e);
    }
    return { level: "", source: "", message: "", excludeScheduler: true };
};

const saveFiltersToStorage = (level, source, message, excludeScheduler) => {
    try {
        localStorage.setItem(
            LOGS_FILTER_STORAGE_KEY,
            JSON.stringify({ level, source, message, excludeScheduler }),
        );
    } catch (e) {
        Logger.error("Error saving logs filter to localStorage:", e);
    }
};

const loadAutoRefreshFromStorage = () => {
    try {
        return localStorage.getItem(AUTO_REFRESH_STORAGE_KEY) === "true";
    } catch (e) {
        Logger.error("Error reading logs auto-refresh from localStorage:", e);
        return false;
    }
};

export default function SysLogsMain() {
    const stored = loadFiltersFromStorage();
    const [searchParams, setSearchParams] = useSearchParams();
    const emailFilter = (searchParams.get("email") || "").trim();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });
    const [totalRows, setTotalRows] = useState(0);
    const [levelFilter, setLevelFilter] = useState(stored.level);
    const [sourceFilter, setSourceFilter] = useState(stored.source);
    const [messageInput, setMessageInput] = useState(stored.message);
    const [messageFilter, setMessageFilter] = useState(stored.message);
    const [distinctSources, setDistinctSources] = useState([]);
    const [sourcesLoading, setSourcesLoading] = useState(true);
    const [excludeScheduler, setExcludeScheduler] = useState(stored.excludeScheduler);
    const [autoRefresh, setAutoRefresh] = useState(loadAutoRefreshFromStorage);
    const [selectedLog, setSelectedLog] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    const fetchLogs = useCallback(async ({ quiet = false } = {}) => {
        if (!quiet) setLoading(true);
        const result = await SystemLogService.list(
            paginationModel.page,
            paginationModel.pageSize,
            "createdAt:desc",
            emailFilter ? null : (levelFilter || null),
            emailFilter ? null : (sourceFilter.trim() || null),
            emailFilter ? false : excludeScheduler,
            null,
            emailFilter || null,
            emailFilter ? null : (messageFilter.trim() || null),
        );
        if (!quiet) setLoading(false);
        if (result.success && result.data) {
            const data = result.data;
            setLogs(data.content || []);
            setTotalRows(data.page?.totalElements ?? 0);
        } else if (!quiet) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Failed to fetch logs" });
        }
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        levelFilter,
        sourceFilter,
        messageFilter,
        excludeScheduler,
        emailFilter,
    ]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setSourcesLoading(true);
            const result = await SystemLogService.listDistinctSources();
            if (cancelled) return;
            setSourcesLoading(false);
            if (result.success) {
                setDistinctSources(result.data || []);
            } else {
                setSnackbar({
                    open: true,
                    severity: "warning",
                    message: result.error || "Could not load source list",
                });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const sourceOptions = useMemo(() => {
        const trimmed = sourceFilter.trim();
        if (trimmed && !distinctSources.includes(trimmed)) {
            return [trimmed, ...distinctSources];
        }
        return distinctSources;
    }, [distinctSources, sourceFilter]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [emailFilter]);

    const handleClearEmailFilter = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("email");
        setSearchParams(next);
    };

    useEffect(() => {
        saveFiltersToStorage(levelFilter, sourceFilter, messageFilter, excludeScheduler);
    }, [levelFilter, sourceFilter, messageFilter, excludeScheduler]);

    const applyMessageSearch = useCallback(() => {
        const next = messageInput.trim();
        setMessageFilter(next);
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [messageInput]);

    useEffect(() => {
        try {
            localStorage.setItem(AUTO_REFRESH_STORAGE_KEY, autoRefresh ? "true" : "false");
        } catch (e) {
            Logger.error("Error saving logs auto-refresh to localStorage:", e);
        }
    }, [autoRefresh]);

    useEffect(() => {
        if (!autoRefresh) return undefined;
        const id = window.setInterval(() => {
            fetchLogs({ quiet: true });
        }, AUTO_REFRESH_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [autoRefresh, fetchLogs]);

    const handleRowClick = (params) => {
        setSelectedLog(params.row);
        setDetailDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        setDeleteDialogOpen(false);
        const count = selectedIds.length;
        setDeleting(true);
        const result = await SystemLogService.deleteByIds(selectedIds);
        setDeleting(false);
        if (result.success) {
            setSelectedIds([]);
            setSnackbar({
                open: true,
                severity: "success",
                message: `${count} log(s) deleted`,
            });
            fetchLogs();
        } else {
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || "Failed to delete logs",
            });
        }
    };

    const handleFilterChange = (filterName, value) => {
        if (filterName === "level") {
            setLevelFilter(value);
        } else if (filterName === "source") {
            setSourceFilter(value);
        } else if (filterName === "excludeScheduler") {
            setExcludeScheduler(value);
        }
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 1 }}>
                <Box>
                    <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                        System Logs
                    </Typography>
                    {emailFilter ? (
                        <Typography variant="body2" color="text.secondary">
                            Send history for this mailing-list email
                        </Typography>
                    ) : null}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                                size="small"
                            />
                        }
                        label="Auto refresh (1 min)"
                    />
                    <Button
                        variant="outlined"
                        onClick={() => fetchLogs()}
                        disabled={loading}
                        sx={{ textTransform: "none" }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={selectedIds.length === 0 || deleting}
                        onClick={() => setDeleteDialogOpen(true)}
                    >
                        Delete ({selectedIds.length})
                    </Button>
                </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                {emailFilter ? (
                    <Chip
                        label={emailFilter}
                        onDelete={handleClearEmailFilter}
                        sx={{ maxWidth: 360 }}
                    />
                ) : null}
                <FormControl size="small" sx={{ minWidth: 140 }} disabled={Boolean(emailFilter)}>
                    <InputLabel id="logs-level-label">Level</InputLabel>
                    <Select
                        labelId="logs-level-label"
                        id="logs-level"
                        value={levelFilter}
                        label="Level"
                        onChange={(e) => handleFilterChange("level", e.target.value)}
                    >
                        <MenuItem value="">
                            <em>All</em>
                        </MenuItem>
                        {SYSTEM_LOG_LEVELS.map((lvl) => (
                            <MenuItem key={lvl} value={lvl}>
                                {lvl}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 220 }} disabled={Boolean(emailFilter)}>
                    <InputLabel id="logs-source-label">Source</InputLabel>
                    <Select
                        labelId="logs-source-label"
                        id="logs-source"
                        value={sourceFilter}
                        label="Source"
                        onChange={(e) => handleFilterChange("source", e.target.value)}
                        disabled={sourcesLoading}
                    >
                        <MenuItem value="">
                            <em>All</em>
                        </MenuItem>
                        {sourceOptions.map((src) => (
                            <MenuItem key={src} value={src}>
                                {src}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    label="Message"
                    placeholder="Search message…"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            applyMessageSearch();
                        }
                    }}
                    disabled={Boolean(emailFilter)}
                    sx={{ minWidth: 260, flex: "1 1 240px", maxWidth: 420 }}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label="Search message"
                                        onClick={applyMessageSearch}
                                        disabled={Boolean(emailFilter)}
                                        edge="end"
                                    >
                                        <SearchIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={emailFilter ? false : excludeScheduler}
                            onChange={(e) => handleFilterChange("excludeScheduler", e.target.checked)}
                            size="small"
                            disabled={Boolean(emailFilter)}
                        />
                    }
                    label="Exclude Scheduler"
                />
            </Box>

            <Box sx={{ width: "100%", minHeight: 400 }}>
                <DataGrid
                    rows={logs}
                    columns={getLogsColumns().filter((column) => column.field !== "details")}
                    getRowId={(row) => row.id}
                    loading={loading}
                    paginationMode="server"
                    rowCount={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[15, 25, 50]}
                    checkboxSelection
                    rowSelectionModel={selectedIds}
                    onRowSelectionModelChange={setSelectedIds}
                    disableRowSelectionOnClick
                    onRowClick={handleRowClick}
                    hideFooterSelectedRowCount
                    autoHeight
                    localeText={{
                        noRowsLabel: emailFilter ? "No send logs for this email." : "No rows",
                    }}
                    sx={{ minHeight: 400, "& .MuiDataGrid-row": { cursor: "pointer" } }}
                />
            </Box>

            <LogDetailDialog
                open={detailDialogOpen}
                onClose={() => {
                    setDetailDialogOpen(false);
                    setSelectedLog(null);
                }}
                log={selectedLog}
            />

            <ConfirmDialog
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                callback={handleConfirmDelete}
                title="Delete logs"
                message={[
                    `Delete ${selectedIds.length} selected log(s)? This cannot be undone.`,
                    "Missing ids on the server are ignored.",
                ]}
                targetName={`${selectedIds.length} log(s)`}
            />

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
